import React, { useEffect, useState } from "react";
import { N8, BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";
import {
  Bell, BellOff, Send, Smartphone, Sun, Moon, Feather, Sparkles, Heart, Check, Droplets,
} from "lucide-react";
import {
  isPushSupported, isIOS, isStandalonePwa, permissionState,
  hasActiveSubscription, subscribeToPush, unsubscribeFromPush,
  fetchPushSettings, updatePushSettings, sendTestPush,
} from "@/lib/push";

/**
 * PushNotificationSettings — the "Notifications" card on the Profile
 * page. Handles subscribe/unsubscribe, per-channel toggles, and per-slot
 * time pickers for the four daily affirmations plus the morning viz,
 * night viz, and today's intention slots.
 *
 * On iOS we detect the not-installed-as-PWA case and show a soft
 * "Add to Home Screen" hint instead of failing silently, because Safari
 * only exposes the Push API from a home-screen install.
 */

const CHANNELS = [
  { key: "affirmations",     Icon: Sparkles, label: "Daily affirmations", body: "Four words a day — a steady rhythm across morning, midday, afternoon, and evening." },
  { key: "morning_viz",      Icon: Sun,      label: "Morning visualization", body: "A quiet moment to begin the day with intention." },
  { key: "night_viz",        Icon: Moon,     label: "Nightly visualization", body: "A soft close to the day. Something to drift into." },
  { key: "today_intention",  Icon: Feather,  label: "Today's intention", body: "The daily nudge to choose today's inner weather." },
  { key: "n8_alerts",        Icon: Heart,    label: <><N8 /> recommendations</>, body: "Occasional wisdom, weekly recaps, community moments." },
  { key: "ritual_reminders", Icon: Bell,     label: "Ritual reminders", body: "The rituals you've scheduled will still buzz at their chosen time." },
  { key: "drink_water",      Icon: Droplets, label: "Drink water", body: "A supportive nudge to sip through the day.", supportsTime: true },
];

export default function PushNotificationSettings() {
  const [supported] = useState(() => isPushSupported());
  const [iosBlocked] = useState(() => isIOS() && !isStandalonePwa());
  const [perm, setPerm] = useState(() => (supported ? permissionState() : "unsupported"));
  const [subscribed, setSubscribed] = useState(false);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  // Load the current subscription status + settings on mount.
  useEffect(() => {
    (async () => {
      if (!supported) return;
      try {
        const has = await hasActiveSubscription();
        setSubscribed(has);
        const data = await fetchPushSettings();
        setSettings(data.settings);
      } catch (err) {
        console.warn("[push] initial load failed:", err);
      }
    })();
  }, [supported]);

  const doSubscribe = async () => {
    setBusy(true);
    try {
      const res = await subscribeToPush();
      if (res.ok) {
        setSubscribed(true);
        setPerm("granted");
        setSettings(res.settings);
        toast.success("You'll hear from N8 clearly, right when it matters.");
      } else if (res.reason === "denied") {
        toast.error("Permission was declined. Turn it on in your browser settings to enable notifications.");
        setPerm("denied");
      } else if (res.reason === "ios-not-installed") {
        toast.info('Add N8One to your Home Screen from Safari — then open it and try again.');
      } else if (res.reason === "unsupported") {
        toast.error("This browser doesn't support push notifications yet.");
      } else {
        toast.error("Couldn't turn notifications on. Please try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  };

  const doUnsubscribe = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast("Notifications turned off. You can turn them back on any time.");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (partial) => {
    // Optimistic update — the network round-trip is trivial but this keeps
    // toggles feeling instant.
    setSettings((s) => ({ ...(s || {}), ...partial }));
    try {
      const res = await updatePushSettings(partial);
      if (res?.settings) setSettings(res.settings);
    } catch (err) {
      toast.error("Couldn't save that change.");
    }
  };

  const setAffirmationTime = (idx, value) => {
    const times = [...(settings?.affirmation_times || ["08:00", "12:00", "16:00", "20:00"])];
    times[idx] = value;
    patch({ affirmation_times: times });
  };

  const doTest = async () => {
    setTesting(true);
    try {
      const res = await sendTestPush();
      if (res.sent > 0) toast.success("Test sent — check your lock screen or notification center.");
      else toast.error("No subscribed device — turn notifications on first.");
    } catch {
      toast.error("Couldn't send test.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <section
      className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-3"
      data-testid="push-notifications-section"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
        <Bell size={12} /> Notifications
      </div>

      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-xl">
          <div className="font-display text-2xl">
            {subscribed ? <><N8 /> nudges you clearly</> : <>Let <N8 /> reach your home screen</>}
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">
            Four daily affirmations, a morning and night visualization, today's intention,
            and occasional nudges from <N8 /> — delivered right to your lock screen.
            You choose the times. Quiet Hours are always respected.
          </p>
        </div>

        {supported && !iosBlocked && (
          <button
            type="button"
            onClick={subscribed ? doUnsubscribe : doSubscribe}
            disabled={busy}
            data-testid="btn-toggle-push"
            aria-pressed={subscribed}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline tab-link disabled:opacity-50 ${
              subscribed
                ? "bg-[hsl(var(--moss))] text-[hsl(var(--paper))] border-transparent"
                : "hover:bg-[hsl(var(--sand))]"
            }`}
          >
            {subscribed ? <Bell size={14} /> : <BellOff size={14} />}
            {busy ? "Working…" : subscribed ? "Notifications on" : "Turn on notifications"}
          </button>
        )}
      </div>

      {/* iOS "install to home screen" hint */}
      {iosBlocked && (
        <div className="mt-6 rounded-md border hairline bg-[hsl(var(--sand))]/40 p-4 flex items-start gap-3" data-testid="push-ios-hint">
          <Smartphone size={18} className="mt-0.5 text-[hsl(var(--clay))]" />
          <div className="text-sm leading-relaxed">
            <div className="font-medium">Enable lock-screen notifications on iPhone</div>
            <p className="text-[hsl(var(--muted-foreground))]">
              1. Tap the <span className="italic">Share</span> icon in Safari · 2. Choose <span className="italic">Add to Home Screen</span>
              · 3. Open <BrandMark signOnMount={false} idleWhisper={false} /> from your home screen · 4. Come back here and tap "Turn on notifications".
            </p>
          </div>
        </div>
      )}

      {!supported && !iosBlocked && (
        <p className="mt-6 text-[11px] text-[hsl(var(--muted-foreground))] italic">
          This browser doesn't support push notifications yet. Try Chrome, Edge, Firefox, or Safari 16.4+.
        </p>
      )}

      {perm === "denied" && (
        <p className="mt-3 text-[11px] text-[hsl(var(--terracotta))]">
          Notifications are blocked in your browser settings. Unblock them for this site to continue.
        </p>
      )}

      {/* Per-channel + per-time settings — only visible after subscribing. */}
      {subscribed && settings && (
        <div className="mt-8 space-y-6" data-testid="push-channels">
          <div className="grid gap-3 sm:grid-cols-2">
            {CHANNELS.map((ch) => (
              <label
                key={ch.key}
                className="soul-card flex items-start gap-3 rounded-md p-4 border hairline bg-[hsl(var(--paper))] cursor-pointer"
                data-testid={`push-channel-${ch.key}`}
              >
                <span
                  aria-hidden
                  className={`shrink-0 grid place-items-center w-9 h-9 rounded-full ${
                    settings[ch.key] ? "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--terracotta))]"
                                     : "bg-[hsl(var(--clay))]/10 text-[hsl(var(--clay))]"
                  }`}
                >
                  <ch.Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-medium">{ch.label}</div>
                    <input
                      type="checkbox"
                      className="ml-auto"
                      checked={!!settings[ch.key]}
                      onChange={(e) => patch({ [ch.key]: e.target.checked })}
                      data-testid={`push-channel-toggle-${ch.key}`}
                    />
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed mt-0.5">{ch.body}</div>
                </div>
              </label>
            ))}
          </div>

          {/* Time pickers — the four affirmations + morning + night + intention */}
          <div className="rounded-md border hairline bg-[hsl(var(--paper))] p-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
              When <N8 /> whispers
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <TimeField label="Today's intention" testid="push-time-today-intention"
                value={settings.today_intention_time || "06:30"}
                onChange={(v) => patch({ today_intention_time: v })}
                disabled={!settings.today_intention} />
              <TimeField label="Morning visualization" testid="push-time-morning-viz"
                value={settings.morning_viz_time || "07:00"}
                onChange={(v) => patch({ morning_viz_time: v })}
                disabled={!settings.morning_viz} />
              <TimeField label="Night visualization" testid="push-time-night-viz"
                value={settings.night_viz_time || "21:30"}
                onChange={(v) => patch({ night_viz_time: v })}
                disabled={!settings.night_viz} />
              {(settings.affirmation_times || ["08:00","12:00","16:00","20:00"]).map((t, i) => (
                <TimeField key={i} label={`Affirmation ${i + 1}`} testid={`push-time-affirmation-${i}`}
                  value={t}
                  onChange={(v) => setAffirmationTime(i, v)}
                  disabled={!settings.affirmations} />
              ))}
            </div>
            <p className="mt-4 text-[11px] italic text-[hsl(var(--muted-foreground))]">
              Times are in your device's local time. Notifications during your Quiet Hours will be held.
            </p>
          </div>

          <div>
            <button
              type="button"
              onClick={doTest}
              disabled={testing}
              data-testid="btn-send-test-push"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.22em] border hairline bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))] tab-link disabled:opacity-50"
            >
              <Send size={12} />
              {testing ? "Sending…" : "Send a test notification"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function TimeField({ label, value, onChange, testid, disabled = false }) {
  return (
    <label className={`block ${disabled ? "opacity-50" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{label}</div>
      <input
        type="time"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-md border hairline bg-[hsl(var(--paper))] px-3 py-2 text-sm"
      />
    </label>
  );
}
